<script setup lang="ts">
import type { DateValue } from 'reka-ui';
import type { HTMLAttributes } from 'vue';
import { CalendarDays } from 'lucide-vue-next';
import { parseDate } from '@internationalized/date';
import { computed, ref } from 'vue';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface Props {
    modelValue?: string | null;
    placeholder?: string;
    disabled?: boolean;
    class?: HTMLAttributes['class'];
    minimalTrigger?: boolean;
    showIcon?: boolean;
    allowClear?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    modelValue: '',
    placeholder: 'Pick a date',
    disabled: false,
    class: undefined,
    minimalTrigger: false,
    showIcon: true,
    allowClear: true,
});

const emit = defineEmits<{
    (e: 'update:modelValue', value: string): void;
}>();

const open = ref(false);

const calendarValue = computed<DateValue | undefined>(() => {
    if (!props.modelValue) return undefined;

    try {
        return parseDate(props.modelValue);
    } catch {
        return undefined;
    }
});

const displayValue = computed(() => {
    if (!props.modelValue) {
        return props.placeholder;
    }

    const date = new Date(`${props.modelValue}T12:00:00`);

    if (Number.isNaN(date.getTime())) {
        return props.placeholder;
    }

    return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    }).format(date);
});

const quickPresets = computed(() => {
    const today = startOfDay(new Date());

    return [
        { label: 'Today', value: toIsoDate(today) },
        { label: 'Tomorrow', value: toIsoDate(addDays(today, 1)) },
        { label: 'Next week', value: toIsoDate(addDays(today, 7)) },
        { label: '2 weeks', value: toIsoDate(addDays(today, 14)) },
        { label: '4 weeks', value: toIsoDate(addDays(today, 28)) },
    ];
});

function handleSelect(value?: DateValue) {
    emit('update:modelValue', value ? value.toString() : '');
    open.value = false;
}

function handlePresetSelect(value: string) {
    emit('update:modelValue', value);
    open.value = false;
}

function clearDate() {
    emit('update:modelValue', '');
    open.value = false;
}

function startOfDay(date: Date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date: Date, days: number) {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
}

function toIsoDate(date: Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function formatPresetValue(value: string) {
    const date = new Date(`${value}T12:00:00`);
    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'short',
    }).format(date);
}
</script>

<template>
    <Popover v-model:open="open">
        <PopoverTrigger as-child>
            <button
                type="button"
                data-slot="date-picker-trigger"
                :disabled="disabled"
                :class="cn(
                    'inline-flex h-[var(--app-control-height)] w-full items-center justify-between text-left font-normal transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                    minimalTrigger
                        ? 'appearance-none rounded-md border-0 bg-transparent px-[var(--app-control-padding-x)] py-1.5 shadow-none hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
                        : 'rounded-xl border border-border/70 bg-background px-[var(--app-control-padding-x)] shadow-none hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                    !modelValue && 'text-muted-foreground',
                    props.class,
                )"
            >
                <span class="truncate">{{ displayValue }}</span>
                <CalendarDays v-if="showIcon" class="size-4 shrink-0 text-muted-foreground" />
            </button>
        </PopoverTrigger>
        <PopoverContent class="w-[22rem] rounded-2xl border-border/70 p-0 shadow-[0_20px_60px_rgba(15,23,42,0.12)] sm:w-[34rem]" align="start">
            <div class="grid gap-0 sm:grid-cols-[13rem_minmax(0,1fr)]">
                <div class="border-b border-border/60 bg-muted/20 p-2 sm:border-b-0 sm:border-r">
                    <div class="space-y-1">
                        <button
                            v-for="preset in quickPresets"
                            :key="preset.value"
                            type="button"
                            class="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm transition-colors hover:bg-background"
                            @click="handlePresetSelect(preset.value)"
                        >
                            <span class="font-medium text-foreground">{{ preset.label }}</span>
                            <span class="text-xs text-muted-foreground">{{ formatPresetValue(preset.value) }}</span>
                        </button>
                    </div>

                    <button
                        v-if="allowClear && modelValue"
                        type="button"
                        class="mt-2 flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
                        @click="clearDate"
                    >
                        <span>Clear date</span>
                        <span class="text-xs">Reset</span>
                    </button>
                </div>

                <div class="p-2">
                    <Calendar
                        :model-value="calendarValue"
                        @update:model-value="handleSelect"
                        initial-focus
                    />
                </div>
            </div>
        </PopoverContent>
    </Popover>
</template>
